import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

/**
 * Compact, presentational pager (zero-based pages). Shows "from–to / total" and prev/next
 * controls, and hides itself when everything fits on one page. Stateless: the parent owns the
 * current {@link page} and reacts to {@link pageChange}.
 */
@Component({
  selector: 'lib-pager',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (total() > 0) {
      <div
        class="mt-2 flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
        <span>{{ from() }}–{{ to() }} / {{ total() }}</span>
        <div class="flex items-center gap-2">
          <button
            type="button"
            class="inline-flex size-7 items-center justify-center rounded-md border border-gray-300 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600 dark:hover:bg-slate-700"
            [disabled]="page() === 0"
            (click)="go(page() - 1)"
            aria-label="Previous">
            <svg class="size-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span class="tabular-nums">{{ page() + 1 }} / {{ pageCount() }}</span>
          <button
            type="button"
            class="inline-flex size-7 items-center justify-center rounded-md border border-gray-300 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600 dark:hover:bg-slate-700"
            [disabled]="page() >= pageCount() - 1"
            (click)="go(page() + 1)"
            aria-label="Next">
            <svg class="size-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    }
  `,
})
export class PagerComponent {
  /** Current zero-based page index. */
  readonly page = input.required<number>();
  /** Page size. */
  readonly pageSize = input.required<number>();
  /** Total element count across all pages. */
  readonly total = input.required<number>();

  /** Emits the requested zero-based page index. */
  readonly pageChange = output<number>();

  protected readonly pageCount = computed(() => Math.max(1, Math.ceil(this.total() / this.pageSize())));
  protected readonly from = computed(() => (this.total() === 0 ? 0 : this.page() * this.pageSize() + 1));
  protected readonly to = computed(() => Math.min(this.total(), (this.page() + 1) * this.pageSize()));

  protected go(p: number): void {
    if (p < 0 || p > this.pageCount() - 1 || p === this.page()) return;
    this.pageChange.emit(p);
  }
}
